/* ── Privacy ────────────────────────────────────────────────────────────────
   A trust layer, not a feature band. Four claims on a pale teal ground, with
   almost all of the work done by typography and space.

   No JS: the page's own [data-reveal] observer (MobileAppLanding) adds .ma-in
   to the section once, and every step of the entrance — eyebrow, rule, heading,
   the four cards, then the icon micro-animations — hangs off that one class
   through its own delay. Each icon animation is written with `both` fill and no
   iteration count, so it plays exactly once and stops where it lands. */

const ICON_SIZE = 26;

/* Padlock — the shackle drops the last millimetre closed as the card arrives. */
const LockIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      className="ma-priv-shackle"
      d="M8.2 10.4V7.3a3.8 3.8 0 0 1 7.6 0v3.1"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <rect x="4.9" y="10.4" width="14.2" height="9.2" rx="2.6" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="14.4" r="1.15" fill="currentColor" />
  </svg>
);

/* Shield with a check that draws itself in once — legitimacy, not legalese. */
const ShieldIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3.2 19.4 6v5.6c0 4.1-3 7.4-7.4 9.2-4.4-1.8-7.4-5.1-7.4-9.2V6z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      className="ma-priv-check"
      d="m8.7 12.1 2.4 2.4 4.2-4.6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Nodes and their links appear first, then the prohibition sweeps across them:
   the data exists, it just never leaves. */
const NoSellIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <g className="ma-priv-net" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="m8.9 10.6 6.2-3.2M8.9 13.4l6.2 3.2" />
      <circle cx="17.2" cy="6.1" r="2.5" />
      <circle cx="6.8" cy="12" r="2.5" />
      <circle cx="17.2" cy="17.9" r="2.5" />
    </g>
    <path
      className="ma-priv-slash"
      d="M4.4 19.6 19.6 4.4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

/* Control, not destruction — the lid lifts a few degrees, one entry drops in,
   the lid settles back. Nothing shakes or slams. */
const DeleteIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="ma-priv-drop" cx="12" cy="4.2" r="1.5" fill="currentColor" />
    <path
      d="M6.9 8.6 7.8 19a1.7 1.7 0 0 0 1.7 1.5h5a1.7 1.7 0 0 0 1.7-1.5l.9-10.4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <g className="ma-priv-lid" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M5 8.6h14" />
      <path d="M10 8.6V7.1h4v1.5" />
    </g>
  </svg>
);

const PRIVACY_CARDS = [
  {
    id: "encryption",
    Icon: LockIcon,
    title: "Encrypted end to end",
    copy: "Your entries are unreadable to anyone but you.",
  },
  {
    id: "dpdp",
    Icon: ShieldIcon,
    title: "DPDP Act 2023 compliant",
    copy: "Built to India's data law, not retrofitted to it.",
  },
  {
    id: "never-sold",
    Icon: NoSellIcon,
    title: "Never sold, ever",
    copy: "No ad networks, no data brokers, no exceptions.",
    lead: true,
  },
  {
    id: "deletion",
    Icon: DeleteIcon,
    title: "Delete it all, anytime",
    copy: "One tap removes your account and everything in it.",
  },
];

export default function PrivacySection() {
  return (
    <section id="ma-privacy" className="ma-band ma-privacy" data-reveal>
      <div className="ma-band-inner">
        <div className="ma-priv-eyebrow">
          <span className="ma-priv-eyebrow-text">Privacy</span>
          <span className="ma-priv-eyebrow-rule" />
        </div>

        {/* One piece, one movement — the accent is colour only, never its own
            animation. */}
        <h2 className="ma-h2 ma-priv-h2">
          Built <span className="ma-h2-grad">private</span>, not patched{" "}
          <span className="ma-h2-grad">private</span>.
        </h2>

        <div className="ma-priv-grid">
          {PRIVACY_CARDS.map(({ id, Icon, title, copy, lead }, i) => (
            <article
              key={id}
              className={`ma-priv-card ma-priv-${id}${lead ? " ma-priv-card-lead" : ""}`}
              style={{ "--ma-delay": `${0.4 + i * 0.09}s`, "--ma-icon-delay": `${0.82 + i * 0.09}s` }}
            >
              <span className="ma-priv-icon">
                <Icon />
              </span>
              <h3 className="ma-priv-title">{title}</h3>
              <p className="ma-priv-copy">{copy}</p>
            </article>
          ))}
        </div>
      </div>

      {/* Ambient, not animated: one broad curve crossing so slowly you are not
          meant to be sure it is moving. */}
      <div className="ma-priv-wave" aria-hidden="true">
        <svg viewBox="0 0 2400 120" preserveAspectRatio="none" focusable="false">
          <path d="M0 74c200-38 400-38 600 0s400 38 600 0 400-38 600 0 400 38 600 0v46H0z" />
        </svg>
      </div>
    </section>
  );
}
